import Axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { USER_INFO } from '@constants/lsconstants';

const API_URL = process.env.REACT_APP_API_URL;

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  const raw = localStorage.getItem(USER_INFO);
  const authenticatedUser = raw ? JSON.parse(raw) : null;
  if (config.headers) {
    if (authenticatedUser?.access_token) {
      config.headers.authorization = `Bearer ${authenticatedUser.access_token}`;
    }
    config.headers.Accept = 'application/json';
  }
  return config;
}

function handleUnauthorized() {
  toast.error('Phiên đăng nhập hết hạn, vui lòng đăng nhập lại');
  sessionStorage.clear();
  localStorage.removeItem(USER_INFO);
  window.location.replace('/user/login');
}

// 403: quyền trên ma trận có thể đã đổi -> store tải lại quyền (đăng ký trong store/index.ts,
// không import store ở đây để tránh vòng import)
let forbiddenHandler: (() => void) | null = null;

export function setForbiddenHandler(handler: () => void) {
  forbiddenHandler = handler;
}

function handleForbidden(error: AxiosError) {
  if (error.response?.status === 403 && !error.config?.url?.includes('/api/rbac/me/')) {
    forbiddenHandler?.();
  }
}

export const axios = Axios.create({ baseURL: API_URL });

export const coreAxios = Axios.create({ baseURL: '/api' });

export function isAxiosError<ResponseType>(
  error: unknown
): error is AxiosError<ResponseType> {
  return Axios.isAxiosError(error);
}

axios.interceptors.request.use(authRequestInterceptor);
axios.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      handleUnauthorized();
    } else if (isAxiosError(error)) {
      handleForbidden(error);
    }
    return Promise.reject(error);
  }
);

coreAxios.interceptors.request.use(authRequestInterceptor);
coreAxios.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      handleUnauthorized();
    } else {
      const message = error.response?.data?.message || error.message;
      toast.error(message || 'session error');
    }
    return Promise.reject(error);
  }
);

export const authService = Axios.create({ baseURL: API_URL });

authService.interceptors.request.use(authRequestInterceptor);
authService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      handleUnauthorized();
    } else if (isAxiosError(error)) {
      handleForbidden(error);
    }
    return Promise.reject(error);
  }
);

export const resourceService = Axios.create({ baseURL: API_URL });

resourceService.interceptors.request.use(authRequestInterceptor);
resourceService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      handleUnauthorized();
    } else if (isAxiosError(error)) {
      handleForbidden(error);
    }
    return Promise.reject(error);
  }
);

// Separate instance for file downloads — does not set Accept header so the
// server can respond with any content-type (e.g. application/octet-stream).
function fileRequestInterceptor(config: InternalAxiosRequestConfig) {
  const raw = localStorage.getItem(USER_INFO);
  const authenticatedUser = raw ? JSON.parse(raw) : null;
  if (config.headers && authenticatedUser?.access_token) {
    config.headers.authorization = `Bearer ${authenticatedUser.access_token}`;
  }
  return config;
}

export const fileService = Axios.create({ baseURL: API_URL });

fileService.interceptors.request.use(fileRequestInterceptor);
fileService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

import { initCacheDevTools } from '../utils/cacheDevTools';
initCacheDevTools([axios, coreAxios, authService, resourceService, fileService]);
